import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquare, Plus } from "lucide-react";
import Layout from "../../components/Layout/Layout";
import LoadingState from "../../components/Common/LoadingState";
import { getMessageSessions, markMessageAsRead } from "../../api/postgre/chatApi";

const formatTime = (value) => {
    if (!value) return "";
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;
    return parsed.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
    });
};

const Messages = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let mounted = true;

        const loadSessions = async (initial = false) => {
            if (initial) setIsLoading(true);
            try {
                const sessionList = await getMessageSessions();
                if (!mounted) return;
                setSessions(sessionList || []);
            } catch {
                // silently fail on poll
            } finally {
                if (mounted && initial) setIsLoading(false);
            }
        };

        loadSessions(true);
        const interval = setInterval(() => loadSessions(false), 15000);
        return () => { mounted = false; clearInterval(interval); };
    }, []);

    const hasMessages = sessions.length > 0;
    const unreadCount = sessions.reduce((total, item) => total + (Number(item.unreadCount) || 0), 0);

    const handleSessionClick = async (messageId) => {
        try {
            await markMessageAsRead(messageId);
        } catch (error) {
            console.error("[handleSessionClick] markMessageAsRead failed:", error);
        }
        navigate(`/messages/${messageId}`);
    };

    if (isLoading) {
        return (
            <Layout activeLabel="Messages">
                <LoadingState message={t("HELP_CHAT_LOADING")} fullScreen />
            </Layout>
        );
    }

    return (
        <Layout activeLabel="Messages">
            <div className="mx-auto max-w-[1100px] pb-24 md:pb-0">
                <div className="mb-8 flex flex-wrap items-end justify-between gap-4 border-b border-slate-200 pb-5">
                    <div>
                        <h1 className="text-2xl font-semibold text-slate-900">
                            {t("MESSAGES_TITLE")}
                            {unreadCount > 0 ? (
                                <span className="ml-2 text-lg font-medium text-primary">
                            {`${unreadCount} ${t("MESSAGES_NEW_SUFFIX")}`}
                                </span>
                            ) : null}
                        </h1>
                        <p className="mt-1 text-sm text-slate-500">
                            {t("MESSAGES_SUBTITLE")}
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate("/messages/new")}
                        className="hidden items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:opacity-95 focus:outline-none md:inline-flex"
                    >
                        <Plus className="h-4 w-4" />
                        {t("MESSAGES_NEW")}
                    </button>
                </div>

                {hasMessages ? (
                    <div className="space-y-3">
                        {sessions.map((session) => {
                            const isUnread = Number(session.unreadCount) > 0;
                            return (
                                <button
                                    key={session.messageId}
                                    type="button"
                                    onClick={() => handleSessionClick(session.messageId)}
                                    className={`flex w-full items-start md:items-center gap-3 rounded-xl border bg-white p-3 text-left transition hover:shadow-sm ${
                                        isUnread ? "border-primary/60 shadow-[0_0_0_1px_rgba(15,27,143,0.12)]" : "border-slate-200"
                                    }`}
                                >
                                    <div className="flex min-w-0 flex-1 items-start md:items-center gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-[7px] font-bold uppercase tracking-[0.08em] text-white sm:h-8 sm:w-8 sm:text-[6px]">
                                            KORRES
                                        </div>
                                        <div className="min-w-0 flex-1 text-sm leading-relaxed text-slate-500">
                                            {session.preview || session.lastMessage}
                                        </div>
                                    </div>
                                    <div className="ml-auto flex shrink-0 items-center justify-between gap-3 pt-0.5">
                                        <span className="shrink-0 text-xs text-slate-400">
                                            {formatTime(session.time)}
                                        </span>
                                    </div>
                                    {Number(session.unreadCount) > 0 ? (
                                        <span className="inline-flex min-w-7 items-center justify-center rounded-full bg-primary px-2 py-1 text-[11px] font-semibold text-white">
                                            {session.unreadCount}
                                        </span>
                                    ) : null}
                                </button>
                            );
                        })}
                    </div>
                ) : (
                    <div className="flex min-h-[50vh] flex-col items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-14 text-center">
                        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <MessageSquare className="h-6 w-6" />
                        </div>
                        <h2 className="mt-5 text-xl font-semibold text-slate-900">
                            {t("MESSAGES_EMPTY_TITLE")}
                        </h2>
                        <p className="mt-2 max-w-md text-sm text-slate-500">
                            {t("MESSAGES_EMPTY_SUBTITLE")}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate("/messages/new")}
                            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-white"
                        >
                            <Plus className="h-4 w-4" />
                            {t("MESSAGES_NEW")}
                        </button>
                    </div>
                )}

                <div className="fixed inset-x-4 bottom-4 z-20 md:hidden">
                    <button
                        type="button"
                        onClick={() => navigate("/messages/new")}
                        className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-primary/20 focus:outline-none"
                    >
                        <Plus className="h-4 w-4" />
                        {t("MESSAGES_NEW")}
                    </button>
                </div>
            </div>
        </Layout>
    );
};

export default Messages;
